/**
 * Staging per-room PURE logic (PF v2 native module, Wave 3).
 *
 * Extracted verbatim from the legacy `RoomInventory` reducer
 * (`components/contexts/RoomInventory.context.tsx:62-88,147-177`), rewritten to
 * operate on the plain-JS `StagingDraftRoomDTO` (arrays / `[k,v][]` pairs) instead
 * of Immutable `Set`/`Map`. NO React, NO store, NO constate — so `staging.dto.ts`
 * (which `modules/completeness.ts` imports) can pull `isRoomCompleted` from here
 * and stay pure.
 *
 * The completeness combos are ported 1:1 — do NOT tighten them. If a recompute
 * ever disagrees with a stored snapshot on a legacy fixture, that is a real legacy
 * bug surfaced; log it, don't "fix" it (wire-compat > correctness here — mirror
 * energy DF-17).
 */

import { StagingType } from 'models/virtualStaging'
import { VirtualFurnitureRoomRemovableOption, VirtualFurnitureRoomType } from 'constants/virtualFurniture'
import type { VirtualFurniturePropertyType } from 'constants/virtualFurniture'

import type { StagingDraftRoomDTO } from './staging.dto'

// ─── Plain-JS Set / Map helpers (replace Immutable Set/Map ops in the lifted UI) ──

/** ImmutableSet.add — append if absent (preserves set semantics on a plain array). */
export const setAdd = <T>(arr: readonly T[], value: T): T[] => (arr.includes(value) ? [...arr] : [...arr, value])

/** ImmutableSet.delete — drop all occurrences. */
export const setDelete = <T>(arr: readonly T[], value: T): T[] => arr.filter((item) => item !== value)

/** ImmutableMap.get over a `[key, value][]` pair array. */
export const mapGet = <K, V>(pairs: ReadonlyArray<[K, V]>, key: K): V | undefined =>
  pairs.find(([k]) => k === key)?.[1]

/** ImmutableMap.set over a `[key, value][]` pair array (replace-or-append). */
export const mapSet = <K, V>(pairs: ReadonlyArray<[K, V]>, key: K, value: V): Array<[K, V]> => [
  ...pairs.filter(([k]) => k !== key),
  [key, value],
]

/** ImmutableMap.delete over a `[key, value][]` pair array. */
export const mapDelete = <K, V>(pairs: ReadonlyArray<[K, V]>, key: K): Array<[K, V]> =>
  pairs.filter(([k]) => k !== key)

// ─── Completeness (pure ports of the RoomInventory reducer) ────────────────────

/**
 * Whether every selected room type has a chosen style template.
 * Legacy: `room.styleCodeMap.size === room.roomTypes.size` (`:62-64`).
 */
export const hasRoomSelectedAllTemplates = (room: StagingDraftRoomDTO): boolean =>
  room.styleCodeMap.length === room.roomTypes.length

/**
 * Whether the room is complete and submittable. Faithful port of the legacy
 * `isRoomCompleted` (`:71-88`) over plain arrays.
 */
export const isRoomCompleted = (room: StagingDraftRoomDTO): boolean => {
  // Room type and furniture removal option must be selected.
  if (!room.roomTypes.length || !room.productId || !room.furnitureRemoval) return false

  const isEmptyRoomStaging =
    room.roomTypes.length === 1 && room.roomTypes.includes(VirtualFurnitureRoomType.EMPTY_ROOM)
  const areRenovationsSelected = room.renovations.length > 0 || room.skipRenovations

  const areTemplatesValid =
    room.type === StagingType.BKBN || isEmptyRoomStaging ? true : hasRoomSelectedAllTemplates(room)

  const isRemovalRenovationComboValid = isEmptyRoomStaging
    ? areRenovationsSelected &&
      (room.furnitureRemoval !== VirtualFurnitureRoomRemovableOption.NONE || room.renovations.length > 0)
    : areRenovationsSelected && !!room.furnitureRemoval

  return areTemplatesValid && isRemovalRenovationComboValid
}

// ─── Default room factory (pure) ───────────────────────────────────────────────

/**
 * Builds a default staging room for a newly-uploaded visual. Faithful port of
 * `createRoomObject` (`:147-177`): all selections empty, `type = CUSTOM`.
 *
 * @param roomKey     - the visual's gcFilename (=== the FileAPI feed `visual.id`).
 * @param productId   - the visual's grouping product id (the staging sentinel).
 * @param sortingOrder- creation order for stable room-list sorting.
 * @param propertyType- resolved from the product kind. v2 keys every
 *                      staging visual under the RESIDENTIAL_STAGING sentinel, so
 *                      this is STANDARD_CATALOG in practice (see the sync hook).
 */
export const makeDefaultRoom = (
  roomKey: string,
  productId: number,
  sortingOrder: number,
  propertyType: VirtualFurniturePropertyType | null
): StagingDraftRoomDTO => ({
  productId,
  key: roomKey,
  // TODO(legacy parity): replace with a translation when/if this name is surfaced.
  name: `Room ${sortingOrder}`,
  roomTypes: [],
  styleCodeMap: [],
  styleCategory: null,
  propertyType,
  decorations: [],
  roomCreationSortingOrder: sortingOrder,
  isComplete: false,
  hasSelectedAllStyleCodes: false,
  renovations: [],
  skipRenovations: false,
  furnitureRemoval: null,
  comment: null,
  type: StagingType.CUSTOM,
})
